import { G } from '@mobily/ts-belt';
import { z } from 'zod';

import { MUTUAL_FUND_ORDER_LIMIT } from '@Common/constants/b2b/limits';
import { ZodAmount, ZodAmountRequest } from '@Common/types/Amount';
import { TAIGA_PREFIX, ZodTaigaId } from '@Common/types/b2b/axa/common';
import { ZodB2bSavingPlanType } from '@Common/types/b2b/plans';
import { FUNDING_DEPOSIT_AMOUNT_INVALID } from '@Common/types/error_messages';
import { validateDepositAmount } from '@Common/types/funding/deposit';
import { ZodB2BPaymentMethod } from '@Common/types/funding/payment_methods';
import { ZodB2bInstrumentNavFrequency } from '@Common/types/instruments/instrument';
import { ZodTransformedProfileSavingPlanId } from '@Common/types/profile/saving_plans';
import { ZodSuccessResponse } from '@Common/types/response';
import { ZodDateTimeString } from '@Common/types/zod/zod-date-string';
import { endpoint, GetEndpointType } from '@Common/types/zod/zod-endpoint';
import { ZodTrimmedString } from '@Common/types/zod/zod-trimmed-string';

export const ZodB2bRecurringInvestmentFrequency = z.enum([
  'weekly',
  'monthly',
  'quarterly',
  'semi_annually',
  'yearly',
]);
export type B2bRecurringInvestmentFrequency = z.infer<typeof ZodB2bRecurringInvestmentFrequency>;

export const VoluntaryDeductibilityValues = ['non_deductible', 'deductible'] as const;
export const ZodVoluntaryDeductibility = z.enum(VoluntaryDeductibilityValues);
export type VoluntaryDeductibility = z.infer<typeof ZodVoluntaryDeductibility>;

export const ZodMutualFundOrderAgreedTerms = z.enum([
  'key_information_documents',
  'amount_certification',
  'source_of_funds_certification',
  'aml_informed',
  'eligibility_confirmation',
  'legal_payment_information',
  'annual_limit',
  'legal_bonus_information',
  'checkout_payment_provider_details',
  'recurring_payment',
]);
export type MutualFundOrderAgreedTerms = z.infer<typeof ZodMutualFundOrderAgreedTerms>;

export const ZodMutualFundOrderAgreedTermsArray = z.array(ZodMutualFundOrderAgreedTerms);

export const ZodMutualFundOrderAllocations = z.array(
  z.object({
    instrument_id: z.number().int(),
    amount: z.number(),
  }),
);
const ZodMutualFundOrderRequestBody = z
  .object({
    saving_plan_id: ZodTransformedProfileSavingPlanId,
    allocations: ZodMutualFundOrderAllocations,
    payment_method: ZodB2BPaymentMethod,
    rib_id: z.number().optional(),
    agreed_terms: ZodMutualFundOrderAgreedTermsArray,
    deductibility: ZodVoluntaryDeductibility.optional(),
  })
  .refine(({ allocations, payment_method, saving_plan_id }) => {
    const orderAmount = allocations.reduce((acc, allocation) => acc + allocation.amount, 0);

    return (
      orderAmount <= MUTUAL_FUND_ORDER_LIMIT &&
      validateDepositAmount(payment_method)({
        amount: orderAmount,
        deposit_context: 'savings',
        // TODO: Check, looks like valid case
        // eslint-disable-next-line @typescript-eslint/no-base-to-string
        is_deposit_limit_raised: String(saving_plan_id).includes(TAIGA_PREFIX),
      })
    );
  }, FUNDING_DEPOSIT_AMOUNT_INVALID);

const ZodMutualFundOrderResponseBody = z.object({
  order_correlation_id: z.uuid().or(ZodTaigaId),
});

export const postExploreMutualFundOrderEndpoint = endpoint({
  method: 'POST',
  body: ZodMutualFundOrderRequestBody,
  responseBody: ZodMutualFundOrderResponseBody,
});

export type PostExploreMutualFundOrderEndpoint = GetEndpointType<
  typeof postExploreMutualFundOrderEndpoint
>;

export const ZodB2BVoluntaryPaymentOrderType = z.enum([
  'voluntary_payment',
  'b2b_recurring_investment',
]);
export type B2BVoluntaryPaymentOrderType = z.infer<typeof ZodB2BVoluntaryPaymentOrderType>;

const ZodMutualFundOrderSummaryCommonRequestBody = z.object({
  saving_plan_id: ZodTransformedProfileSavingPlanId,
  start_date: ZodDateTimeString.optional(),
  frequency: ZodB2bRecurringInvestmentFrequency.optional(),
  payment_method: ZodB2BPaymentMethod.optional(),
  rib_id: z.number().optional(),
  order_type: ZodB2BVoluntaryPaymentOrderType.optional(),
});

export const ZodMutualFundOrderSummaryRequestBody = z
  .object({
    ...ZodMutualFundOrderSummaryCommonRequestBody.shape,
    total_amount: ZodAmountRequest,
  })
  .or(
    z.object({
      ...ZodMutualFundOrderSummaryCommonRequestBody.shape,
      allocations: z.array(
        z.object({
          instrument_id: z.number().int(),
          amount: z.number(),
        }),
      ),
    }),
  )
  .refine(
    ({ saving_plan_id, frequency, start_date }) =>
      // ER start dates are computed by the provider (BR_API240_230), only internal plans link them
      G.isObject(saving_plan_id)
        ? (G.isNullable(frequency) && G.isNullable(start_date)) ||
          (G.isNotNullable(frequency) && G.isNotNullable(start_date))
        : true,
    'frequency_and_start_date_are_linked_input',
  );

export type MutualFundOrderSummaryRequestBody = z.infer<
  typeof ZodMutualFundOrderSummaryRequestBody
>;

export const ZodEntryFee = z.object({
  amount_gross: ZodAmount,
  employee_amount: ZodAmount,
  company_amount: ZodAmount,
  company_rate_percentage: z.number(),
  rate_percentage: z.number(),
});

export const ZodMatchingContributionOrderTaxes = z.object({
  csg_tax_rate: z.number().describe('CSG tax rate as a decimal fraction, e.g. 0.092 for 9.2%'),
  csg_tax_amount: ZodAmount,
  crds_tax_rate: z.number().describe('CRDS tax rate as a decimal fraction, e.g. 0.005 for 0.5%'),
  crds_tax_amount: ZodAmount,
  total_tax_amount: ZodAmount,
});

export type EntryFee = z.infer<typeof ZodEntryFee>;

const ZodMutualFundOrderSummaryResponseBody = z
  .strictObject({
    saving_plan_type: ZodB2bSavingPlanType,
    allocations: z
      .object({
        instrument_id: z.number(),
        amount_gross: ZodAmount,
        entry_fee: ZodEntryFee.nullable(),
      })
      .array(),
    allocation_count: z.number(),
    matching_contribution: z
      .object({
        amount: ZodAmount,
        gross_amount: ZodAmount,
        tax_amount: ZodAmount,
        company_name: ZodTrimmedString,
        tax_info: z.object({
          csg_tax_rate: z.number(),
          csg_tax_amount: ZodAmount,
          crds_tax_rate: z.number(),
          crds_tax_amount: ZodAmount,
        }),
      })
      .nullable(),
    service_fee: ZodAmount.nullable(),
    total_value_net: ZodAmount,
    total_value_gross: ZodAmount,
    total_value_gross_with_contribution: ZodAmount,
    entry_fee: ZodAmount.nullable(),
    should_acknowledge_eligibility: z.boolean(),
    terms_to_comply: ZodMutualFundOrderAgreedTermsArray,
    start_date: ZodDateTimeString.nullable(),
    frequency: ZodB2bRecurringInvestmentFrequency.nullable(),
    allocations_executions: z
      .object({
        instrument_id: z.number(),
        amount_gross: ZodAmount,
        execution_date: ZodDateTimeString,
        nav_frequency: ZodB2bInstrumentNavFrequency.nullable(),
      })
      .array()
      .nullable(),
  })
  .refine(({ allocations, allocation_count }) => allocations.length === allocation_count, {
    message: 'the amount of allocations must be equal to allocations_count',
  });

export const postExploreMutualFundOrderSummaryEndpoint = endpoint({
  method: 'POST',
  body: ZodMutualFundOrderSummaryRequestBody,
  responseBody: ZodMutualFundOrderSummaryResponseBody,
});

export type PostExploreMutualFundOrderSummaryEndpoint = GetEndpointType<
  typeof postExploreMutualFundOrderSummaryEndpoint
>;

const ZodMutualFundOrderFundingStatusBody = z.object({
  order_correlation_id: z.uuid(),
  is_funded: z.boolean(),
});

export const postExploreMutualFundOrderFundingStatusEndpoint = endpoint({
  method: 'POST',
  body: ZodMutualFundOrderFundingStatusBody,
  responseBody: ZodSuccessResponse,
});

export type PostExploreMutualFundOrderFundingStatusEndpoint = GetEndpointType<
  typeof postExploreMutualFundOrderFundingStatusEndpoint
>;

const ZodMutualFundOrderLastExecutedAllocationRequestBody = z.object({
  amount: ZodAmountRequest,
  saving_plan_id: ZodTransformedProfileSavingPlanId,
});

const ZodMutualFundOrderLastExecutedAllocationResponseBody = z.object({
  allocations: z
    .object({
      instrument_id: z.number(),
      amount: ZodAmount,
    })
    .array()
    .describe(
      'Previous allocation split over `amount`, to prefill a voluntary payment. ' +
        'Restricted to instruments still tradable and available for `voluntary_deposit`.',
    ),
});

export const postExploreMutualFundOrderLastExecutedAllocationEndpoint = endpoint({
  method: 'POST',
  body: ZodMutualFundOrderLastExecutedAllocationRequestBody,
  responseBody: ZodMutualFundOrderLastExecutedAllocationResponseBody,
});

export type PostExploreMutualFundOrderLastExecutedAllocationEndpoint = GetEndpointType<
  typeof postExploreMutualFundOrderLastExecutedAllocationEndpoint
>;
